// packages/engine/src/process-test-eval.ts
//
// P2 — the assertion evaluator over the enumerated trace-set. Given a `test`'s
// `when` pins and `then` assertions, materialise the pin-constrained trace-set T
// (via {@link enumerateTraces}, P1a+P1c) and evaluate each assertion against it:
//
//   - occurrence (□ / □¬ / ◇): `happens` = ∀ trace ≥1, `never` = ∀ trace =0,
//     `sometimes` = ∃ trace ≥1. An exact/comparator `count` refines the per-trace
//     predicate (`happens 2` ⇒ ∀ trace: count =2; `sometimes >= 3` ⇒ ∃ trace:
//     count ≥3).
//   - terminator (`fail`/`finish [all] ["…"]`): ∀ trace the trace's END matches
//     that outcome — reusing the producer's per-trace `end` model (the realised
//     `processEnds` outcome), incl. the `fail all`/`finish all` dynamic-host `all`.
//   - positional order: the `then` sequence is a per-trace SUBSEQUENCE, occurrence
//     implied (an absent earlier item fails the order). Two adjacent positional
//     refs that land on CONCURRENT parallel-branch steps have no defined order →
//     the order claim is OPEN, not a pass.
//   - `parallel { … }` group: an order-free block — every member must occur
//     (□ each), no order among them; the group occupies one positional slot.
//
// OPEN is never a silent pass (design §): when the trace-set is incomplete (a
// bounded loop/`go`, an OPEN cap, or an unresolved select/parallel outcome), a
// universally-quantified assertion that HOLDS on the known traces is OPEN (can't
// be proven over the traces we didn't enumerate); an existential that found its
// witness is PROVED regardless. A definitive refutation on a known trace is
// always FAIL. A `time` budget is P4 — reported OPEN here, never silently passed.

import type { Diagnostic } from "./types.js";
import type { ProcessModel } from "./process-model.js";
import type { WalkerContext, WalkStep } from "./process-walker-types.js";
import type {
  TestAssertion,
  TestOccurrence,
  TestTerminator,
  TestParallelGroup,
  TestRef,
  TestCompareOp,
} from "./v010-ast.js";
import { enumerateTraces, type EnumerateOptions } from "./process-trace-enumerator.js";
import type { Trace, TraceEvent } from "./process-trace-producer.js";
import type { ProcessNode } from "./process-model.js";
import { estimateProcess, type LatencyEnv, type Injected } from "./process-latency.js";
import type { TestTimeBudget } from "./v010-ast.js";
import { parseDuration } from "@archlang/calc";

/** One assertion's verdict. `open` = unprovable over the enumerated traces
 *  (an incomplete trace-set or a structurally-undecidable claim) — NOT a pass. */
export interface AssertionResult {
  readonly assertion: TestAssertion;
  readonly status: "pass" | "fail" | "open";
  /** Human-readable why, for `fail`/`open`. */
  readonly detail?: string;
}

/** An OPEN dimension of the test run — an incomplete trace-set (bounded loop /
 *  `go` / cap, unresolved select/parallel) or an undecidable assertion. */
export interface TestOpenItem {
  readonly reason: string;
  readonly detail?: string;
}

/** The result of running one `test` over its trace-set. `pass` iff EVERY
 *  assertion passed — an `open` (unprovable) assertion leaves `pass` false. */
export interface TestResult {
  readonly pass: boolean;
  readonly perAssertion: readonly AssertionResult[];
  readonly open: readonly TestOpenItem[];
  readonly traceCount: number;
  readonly diagnostics: readonly Diagnostic[];
}

/** The pin/assertion inputs of a `test` — the subset {@link evalTest} consumes
 *  (a {@link TestDecl}, or a hand-built pair in a unit test). */
export interface TestSpec {
  readonly pins: readonly import("./v010-ast.js").TestPin[];
  readonly assertions: readonly TestAssertion[];
}

/**
 * Evaluate a `test`'s assertions over its pin-constrained trace-set. Enumerates
 * T once, then decides each assertion (occurrence/terminator/parallel-group +
 * positional order). The evaluator is pure — no reference resolution beyond the
 * ref↔step matching below; the process must already be resolved.
 */
export function evalTest(
  process: ProcessModel,
  spec: TestSpec,
  ctx: WalkerContext,
  opts: EnumerateOptions = {},
): TestResult {
  const en = enumerateTraces(process, spec.pins, ctx, opts);
  const traces = en.traces;
  const m = createMatcher(en.spliceLabels);
  // The trace-set is incomplete when a loop/`go`/cap was bounded, or a
  // select/parallel outcome rode at its structural default (its other outcomes
  // are not in T). Either way a universal claim can't be proven over T.
  const setOpen = en.open.length > 0 || en.openBounds.length > 0;

  const open: TestOpenItem[] = [];
  for (const b of en.openBounds) {
    open.push({ reason: b.reason, ...(b.nodeId ? { detail: b.nodeId } : {}) });
  }
  for (const c of en.open) {
    open.push({ reason: `unresolved-${c.kind}`, detail: c.containerNodeId });
  }

  // The positional items (in document order) — occurrence(happens)/terminator/
  // parallel-group. `never`/`sometimes`/`time` are NOT positional (absence /
  // existence / timing carry no place in the ordered spine).
  const positional = spec.assertions.filter(isPositional);

  // Per-item order status across every trace (worst wins).
  const orderStatus = evalOrder(traces, positional, m);

  const budget: BudgetCtx = { env: en.env, inject: en.inject, process };
  const perAssertion: AssertionResult[] = spec.assertions.map((a) => {
    const intrinsic = evalIntrinsic(a, traces, setOpen, m, budget);
    if (!isPositional(a)) return intrinsic;
    const ord = orderStatus.get(a) ?? { status: "pass" as const };
    return worst(intrinsic, { assertion: a, status: ord.status, detail: ord.detail });
  });

  for (const r of perAssertion) {
    if (r.status === "open") open.push({ reason: "unprovable-assertion", detail: r.detail });
  }

  return {
    pass: perAssertion.every((r) => r.status === "pass"),
    perAssertion,
    open,
    traceCount: traces.length,
    diagnostics: en.diagnostics,
  };
}

// -----------------------------------------------------------------------------
// ref ↔ step matching (splice-scope-aware, design §6)
// -----------------------------------------------------------------------------

/** The dotted path a {@link TestRef} names, e.g. `["Payments","charge"]`. */
function refPath(ref: TestRef): string[] {
  return ref.segments.map((s) => s.value);
}

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** A ref resolved against the `do`-splice namespace: the leading label-path
 *  segments (`pay1`, `out.innr`) stripped off, leaving the step `path`; when a
 *  splice was named, `nodeIdPrefix` scopes matching to steps inlined under it. */
interface ScopedRef {
  readonly path: string[];
  readonly nodeIdPrefix?: string;
}

/** A splice-scope-aware step matcher. Resolves each ref's leading `do`-label
 *  prefix ONCE (against `spliceLabels`: dotted label path → splice container
 *  nodeId) and scopes matches to the steps inlined under that splice. Bare refs
 *  (no known label prefix) match anywhere — the single-site / un-namespaced
 *  case. */
function createMatcher(spliceLabels: ReadonlyMap<string, string>): {
  refMatchesStep: (ref: TestRef, step: WalkStep) => boolean;
  countInTrace: (ref: TestRef, trace: Trace) => number;
  firstMatchAfter: (events: readonly TraceEvent[], after: number, ref: TestRef) => number;
} {
  const cache = new Map<TestRef, ScopedRef>();

  // Strip the LONGEST leading label-path prefix that names a splice — so nested
  // `out.innr.x` scopes to `innr`, not `out`. The remainder is the step path.
  const resolve = (ref: TestRef): ScopedRef => {
    const hit = cache.get(ref);
    if (hit) return hit;
    const segs = refPath(ref);
    let scoped: ScopedRef = { path: segs };
    if (spliceLabels.size > 0) {
      for (let k = Math.min(segs.length - 1, segs.length); k >= 1; k--) {
        const labelPath = segs.slice(0, k).join(".");
        const container = spliceLabels.get(labelPath);
        if (container !== undefined) {
          scoped = { path: segs.slice(k), nodeIdPrefix: `${container}/body` };
          break;
        }
      }
    }
    cache.set(ref, scoped);
    return scoped;
  };

  /**
   * Does a step realise the assertion's step reference? A call step matches when
   * the (scope-stripped) ref path equals the callee path (`Payments.charge`) or
   * its suffix (a bare `charge` = the interface tail); a single-segment ref also
   * matches the step's `as` alias. A note step matches a single-string ref
   * against its text. A splice-namespaced ref additionally requires the step to
   * sit under that splice's inlined body.
   */
  const refMatchesStep = (ref: TestRef, step: WalkStep): boolean => {
    const { path, nodeIdPrefix } = resolve(ref);
    if (path.length === 0) return false;
    if (nodeIdPrefix !== undefined && !step.nodeId.startsWith(nodeIdPrefix)) return false;
    // Note action — matched by its text (a bare positional string in `then`).
    if (step.noteText !== undefined) {
      return path.length === 1 && step.noteText === path[0];
    }
    // `as` alias — a single-segment ref can name the aliased step directly.
    if (path.length === 1 && step.asName === path[0]) return true;
    const to = step.to;
    if (arraysEqual(path, to)) return true;
    // Suffix match: the ref is the tail of the callee path (bare interface name).
    if (path.length < to.length && arraysEqual(path, to.slice(to.length - path.length))) return true;
    return false;
  };

  const countInTrace = (ref: TestRef, trace: Trace): number => {
    let n = 0;
    for (const s of trace.steps) if (refMatchesStep(ref, s)) n++;
    return n;
  };

  const firstMatchAfter = (events: readonly TraceEvent[], after: number, ref: TestRef): number => {
    for (let i = after + 1; i < events.length; i++) {
      const e = events[i]!;
      if (e.kind === "step" && refMatchesStep(ref, e)) return i;
    }
    return -1;
  };

  return { refMatchesStep, countInTrace, firstMatchAfter };
}

type Matcher = ReturnType<typeof createMatcher>;

// -----------------------------------------------------------------------------
// intrinsic (order-free) assertion verdicts
// -----------------------------------------------------------------------------

function compare(op: TestCompareOp, lhs: number, rhs: number): boolean {
  switch (op) {
    case "=": return lhs === rhs;
    case ">=": return lhs >= rhs;
    case "<=": return lhs <= rhs;
    case ">": return lhs > rhs;
    case "<": return lhs < rhs;
  }
}

/** The env + pin-injection + process the time-budget evaluator folds against. */
interface BudgetCtx {
  readonly env: LatencyEnv;
  readonly inject: ReadonlyMap<ProcessNode, Injected>;
  readonly process: ProcessModel;
}

/** Consecutive-identity dedup: a chained call repeats its one `CallNode` per hop
 *  in `stepNodes` — collapse those so the pruned fold prices the chain (head
 *  caps tail), not a run of independent calls. */
function prunedFlow(stepNodes: readonly ProcessNode[]): ProcessNode[] {
  const out: ProcessNode[] = [];
  for (const n of stepNodes) if (out[out.length - 1] !== n) out.push(n);
  return out;
}

/**
 * `time system|business <op> <dur>` (design §9). Because the trace is pinned, a
 * pruned Flow of exactly the steps that ran, re-folded via `estimateProcess`
 * under the same injection, yields a concrete per-axis worst-case. A □ budget
 * holds iff EVERY trace's worst-case satisfies the comparator; an OPEN axis on
 * the path is unprovable → **fail** (you cannot guarantee `<1s` over an
 * unbounded gap). Axis words mirror `@estimatedLatency.system` / `.business`.
 */
function evalTimeBudget(a: TestTimeBudget, traces: readonly Trace[], budget: BudgetCtx): AssertionResult {
  if (a.axis !== "system" && a.axis !== "business") {
    return { assertion: a, status: "fail", detail: `unknown time axis '${a.axis}' — use \`system\` or \`business\`` };
  }
  const target = parseDuration(a.duration.trim());
  if (!target.ok) {
    return { assertion: a, status: "fail", detail: `unparseable budget duration '${a.duration}'` };
  }
  const threshold = target.hi;
  for (const t of traces) {
    const est = estimateProcess({ ...budget.process, body: prunedFlow(t.stepNodes) }, budget.env, { inject: budget.inject });
    const bound = a.axis === "business" ? est.timed.business : est.timed.system;
    if (bound.open) {
      // Unprovable, NOT refuted: an OPEN axis on the path (no latency data /
      // unbounded flow) can't be guaranteed under the budget — report OPEN, never
      // a silent pass and never a false FAIL (the module's OPEN invariant). `pass`
      // stays false either way; `evalTest` folds this into `result.open[]`.
      return { assertion: a, status: "open", detail: `${a.axis} time is OPEN on a path (unprovable — no latency data / unbounded flow)` };
    }
    if (!compare(a.op, bound.hi, threshold)) {
      return { assertion: a, status: "fail", detail: `${a.axis} worst-case ${bound.hi}s violates ${a.op} ${threshold}s` };
    }
  }
  return { assertion: a, status: "pass" };
}

function evalIntrinsic(a: TestAssertion, traces: readonly Trace[], setOpen: boolean, m: Matcher, budget: BudgetCtx): AssertionResult {
  switch (a.kind) {
    case "occurrence": return evalOccurrence(a, traces, setOpen, m);
    case "terminator": return evalTerminator(a, traces, setOpen);
    case "parallelGroup": return evalParallelGroup(a, traces, setOpen, m, budget);
    case "time":
      return evalTimeBudget(a, traces, budget);
  }
}

/** The per-trace occurrence predicate: `count op n`, or bare ≥1. */
function occHolds(a: TestOccurrence, trace: Trace, m: Matcher): boolean {
  const c = m.countInTrace(a.ref, trace);
  if (a.modal === "never") return c === 0;
  if (a.count) return compare(a.count.op, c, a.count.n);
  return c >= 1;
}

function evalOccurrence(a: TestOccurrence, traces: readonly Trace[], setOpen: boolean, m: Matcher): AssertionResult {
  const label = describeRef(a.ref);
  if (a.modal === "sometimes") {
    // ◇ — existential. A witness proves it regardless of an OPEN set.
    const hit = traces.some((t) => occHolds({ ...a, modal: undefined }, t, m));
    if (hit) return { assertion: a, status: "pass" };
    return setOpen
      ? { assertion: a, status: "open", detail: `no trace has ${label} yet, but the trace-set is incomplete` }
      : { assertion: a, status: "fail", detail: `${label} never occurs in any trace` };
  }
  if (a.modal === "never") {
    // □¬ — universal absence. A hit refutes it definitively.
    const bad = traces.find((t) => m.countInTrace(a.ref, t) > 0);
    if (bad) return { assertion: a, status: "fail", detail: `${label} occurs but was asserted never` };
    return setOpen
      ? { assertion: a, status: "open", detail: `${label} is absent in every known trace, but the set is incomplete` }
      : { assertion: a, status: "pass" };
  }
  // □ (happens / count) — universal. A miss refutes it definitively.
  const bad = traces.find((t) => !occHolds(a, t, m));
  if (bad) return { assertion: a, status: "fail", detail: `${label} does not hold in every trace` };
  if (traces.length === 0) return { assertion: a, status: "fail", detail: "no traces enumerated" };
  return setOpen
    ? { assertion: a, status: "open", detail: `${label} holds on every known trace, but the set is incomplete` }
    : { assertion: a, status: "pass" };
}

/** Does the trace's END realise the terminator assertion? */
function terminatorMatches(a: TestTerminator, trace: Trace): boolean {
  const e = trace.end;
  if (!e) return false; // fell through to implicit "complete" — no fail/finish
  if (e.type !== a.which) return false;
  if (a.all && !e.all) return false;
  if (a.label !== undefined && e.reason !== a.label) return false;
  return true;
}

function evalTerminator(a: TestTerminator, traces: readonly Trace[], setOpen: boolean): AssertionResult {
  const label = `${a.which}${a.all ? " all" : ""}${a.label ? ` "${a.label}"` : ""}`;
  const bad = traces.find((t) => !terminatorMatches(a, t));
  if (bad) return { assertion: a, status: "fail", detail: `a trace does not end with ${label}` };
  if (traces.length === 0) return { assertion: a, status: "fail", detail: "no traces enumerated" };
  return setOpen
    ? { assertion: a, status: "open", detail: `every known trace ends with ${label}, but the set is incomplete` }
    : { assertion: a, status: "pass" };
}

/** A `parallel { … }` group — order-free: every member holds (□), no order. */
function evalParallelGroup(a: TestParallelGroup, traces: readonly Trace[], setOpen: boolean, m: Matcher, budget: BudgetCtx): AssertionResult {
  const results = a.assertions.map((member) => evalIntrinsic(member, traces, setOpen, m, budget));
  const failed = results.find((r) => r.status === "fail");
  if (failed) return { assertion: a, status: "fail", detail: `parallel member failed: ${failed.detail ?? ""}` };
  const opened = results.find((r) => r.status === "open");
  if (opened) return { assertion: a, status: "open", detail: `parallel member unprovable: ${opened.detail ?? ""}` };
  return { assertion: a, status: "pass" };
}

// -----------------------------------------------------------------------------
// positional order
// -----------------------------------------------------------------------------

/** Positional items are the ordered spine: a `happens`/count occurrence, a
 *  terminator, or a `parallel` group. `never`/`sometimes`/`time` carry no place. */
function isPositional(a: TestAssertion): boolean {
  if (a.kind === "occurrence") return a.modal === undefined || a.modal === "happens";
  return a.kind === "terminator" || a.kind === "parallelGroup";
}

interface OrderVerdict {
  readonly status: "pass" | "fail" | "open";
  readonly detail?: string;
}

/**
 * Verify the positional subsequence in every trace. Returns, per positional
 * assertion, the WORST verdict across traces: a definitive out-of-order /
 * absent placement in a known trace is `fail`; a placement on a step CONCURRENT
 * with the prior item (no defined order) is `open`; else `pass`.
 */
function evalOrder(traces: readonly Trace[], positional: readonly TestAssertion[], m: Matcher): Map<TestAssertion, OrderVerdict> {
  const worstByItem = new Map<TestAssertion, OrderVerdict>();
  const bump = (a: TestAssertion, v: OrderVerdict): void => {
    const cur = worstByItem.get(a);
    if (!cur || rank(v.status) > rank(cur.status)) worstByItem.set(a, v);
  };
  for (const a of positional) worstByItem.set(a, { status: "pass" });

  for (const trace of traces) {
    let cursor = -1;
    let prevStep: WalkStep | undefined;
    for (const a of positional) {
      if (a.kind === "terminator") {
        // The end is positionally last; a match keeps the order valid.
        continue;
      }
      if (a.kind === "parallelGroup") {
        // Every member must occur after the cursor; order-free among them.
        let maxIdx = cursor;
        let missing = false;
        for (const member of a.assertions) {
          if (member.kind !== "occurrence") continue;
          const idx = m.firstMatchAfter(trace.events, cursor, member.ref);
          if (idx === -1) missing = true;
          else maxIdx = Math.max(maxIdx, idx);
        }
        if (missing) bump(a, { status: "fail", detail: "a parallel-group member is out of order or absent" });
        cursor = maxIdx;
        prevStep = maxIdx >= 0 ? (trace.events[maxIdx] as WalkStep) : prevStep;
        continue;
      }
      // occurrence (positional)
      const ref = (a as TestOccurrence).ref;
      const idx = m.firstMatchAfter(trace.events, cursor, ref);
      if (idx === -1) {
        const anyIdx = m.firstMatchAfter(trace.events, -1, ref);
        if (anyIdx === -1) {
          bump(a, { status: "fail", detail: `${describeRef(ref)} is absent (ordering implies occurrence)` });
        } else if (prevStep && concurrent(trace.events[anyIdx] as WalkStep, prevStep)) {
          bump(a, { status: "open", detail: `${describeRef(ref)} is concurrent with the prior step — no defined order` });
        } else {
          bump(a, { status: "fail", detail: `${describeRef(ref)} occurs out of positional order` });
        }
        // cursor unchanged — a later item still checks against the last placed step.
      } else {
        const step = trace.events[idx] as WalkStep;
        if (prevStep && concurrent(step, prevStep)) {
          bump(a, { status: "open", detail: `${describeRef(ref)} is concurrent with the prior step — no defined order` });
        }
        cursor = idx;
        prevStep = step;
      }
    }
  }
  return worstByItem;
}

/** Two steps are concurrent when they sit in DIFFERENT branches of the same
 *  parallel container — their relative order in the linearised trace is
 *  arbitrary, so no positional claim between them can be proven. */
function concurrent(a: WalkStep, b: WalkStep): boolean {
  const pg = a.modifiers.parallelGroup;
  if (!pg || b.modifiers.parallelGroup !== pg) return false;
  const ba = branchIndex(a.nodeId, pg);
  const bb = branchIndex(b.nodeId, pg);
  return ba !== undefined && bb !== undefined && ba !== bb;
}

/** The `/branch:N` index a step sits under, within its `parallelGroup` container. */
function branchIndex(nodeId: string, parallelGroup: string): number | undefined {
  if (!nodeId.startsWith(parallelGroup)) return undefined;
  const m = nodeId.slice(parallelGroup.length).match(/^\/branch:(\d+)/);
  return m ? Number.parseInt(m[1]!, 10) : undefined;
}

// -----------------------------------------------------------------------------
// helpers
// -----------------------------------------------------------------------------

function rank(s: AssertionResult["status"]): number {
  return s === "fail" ? 2 : s === "open" ? 1 : 0;
}

/** Combine an intrinsic verdict `a` with an order verdict `b` — the worse
 *  (fail > open > pass) wins; on an equal non-pass rank the order verdict `b`
 *  is preferred (its detail is the more specific "why"). */
function worst(a: AssertionResult, b: AssertionResult): AssertionResult {
  const ra = rank(a.status);
  const rb = rank(b.status);
  if (rb > ra) return b;
  if (rb === ra && b.status !== "pass") return b;
  return a;
}

function describeRef(ref: TestRef): string {
  return refPath(ref).join(".");
}
